import React, { FC, useState } from "react";
import { Button, Typography } from "@material-ui/core";
import { Cancel, CheckCircle, CloudDownload } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";

import BaseDataGrid from "../../../../components/modules/BaseDataGrid";
import ConfirmationModal from "../../../../components/modules/ConfirmationModal";
import Loader from "../../../../components/elements/Loader";
import Switch from "../../../../components/elements/Switch";
import classes from "./CompaniesDashboard.module.scss";
import csvDownload from "../../../../utils/csvExport";
import { FoundCompanyInfo } from "../../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { setSegmentsInclude } from "../../../../redux/reducers/campaignToCreate";
import { statusColors } from "../../../../theme/brandgenColorPalette";

type CompaniesDashboardProps = {
  tierValue: string[];
  areTargetAccountsOnly: boolean;
  areCompaniesLoading: boolean;
  primarySegments: FoundCompanyInfo[];
  uniqueSegments: FoundCompanyInfo[];
};

let hasShownCompanyFeeModal = false;

const CompaniesDashboard: FC<CompaniesDashboardProps> = ({ areCompaniesLoading, primarySegments, uniqueSegments }) => {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState<boolean>(!hasShownCompanyFeeModal);

  const fromFile = useSelector((state: ReduxApplicationState) => state.campaignToCreate.foundCompanyInfo?.fromFile);

  const handleLineItemActiveStatusChange = (domain: string, include: boolean) => () => {
    dispatch(setSegmentsInclude(domain, !include));
  };

  const columns = [
    { field: "domain", headerName: "Domain", flex: 1, disableColumnMenu: true },
    {
      field: "include",
      headerName: "Include",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      // eslint-disable-next-line react/display-name
      renderCell: (params: any) => (
        <Switch
          checked={params.row.include}
          onChange={handleLineItemActiveStatusChange(params.row.domain, params.row.include)}
        />
      ),
    },
    {
      field: "matched",
      headerName: "Matched",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      // eslint-disable-next-line react/display-name
      renderCell: (params: any) =>
        params.value ? <CheckCircle htmlColor={statusColors.success} /> : <Cancel htmlColor={statusColors.error} />,
    },
  ];

  const rows = primarySegments.concat(uniqueSegments).map((segment, index) => ({
    id: `${segment.domain}-${index}`,
    domain: segment.domain,
    matched: !!segment.domainInfo,
    include: !!segment.include,
  }));

  const totalMatches =
    (primarySegments || []).filter((segment) => segment.domainInfo).length +
    (uniqueSegments || []).filter((segment) => segment.domainInfo).length;

  return (
    <div className={classes.companiesDashboardContainer}>
      {areCompaniesLoading ? (
        <div className={classes.loaderContainer}>
          <Typography>We're getting your company information. This may take a few minutes...</Typography>
          <Loader isAbsolutePosition={false} size={42} />
        </div>
      ) : null}

      {rows.length > 0 && !areCompaniesLoading ? (
        <div className={classes.dashboardContainer}>
          <div className={classes.dashboardTextInfo}>
            <div>
              <Typography className={classes.matchedCompaniesInfo}>
                We were able to match{" "}
                <strong>
                  {totalMatches} out of {primarySegments.length + uniqueSegments.length}
                </strong>{" "}
                companies.
                {!fromFile &&
                  " The domains that we use for data collection are pulled from the 'domain' property associated with the company in HubSpot. If the domain listed below appears incorrect, update this property in HubSpot and then pull the list again."}
              </Typography>

              {primarySegments && primarySegments.length > 0 && (
                <Typography className={classes.subdomainsWarning}>
                  Currently, our targeting features do not extend into targeting subdomains (example:
                  subdomain.primarydomain.com). Selecting to include reference to a subdomains in the targeting
                  inclusion list will result in targeting the primary domain. (example: subdomain.primarydomain.com).
                </Typography>
              )}
            </div>
            <div>
              <Button
                style={{
                  marginBottom: "20px",
                  float: "right",
                }}
                variant="contained"
                startIcon={<CloudDownload />}
                onClick={() => {
                  csvDownload({
                    data: rows.map((row) => ({
                      Domain: row.domain,
                      Matched: row.matched ? "Yes" : "No",
                    })),
                    filename: "export.csv",
                    delimiter: ",",
                  });
                }}
              >
                Export
              </Button>
            </div>
          </div>

          <BaseDataGrid columns={columns} rows={rows} autoHeight disableSelectionOnClick />

          <ConfirmationModal
            isOpen={openModal}
            confirmTitle="Note"
            confirmQuestion="Companies added here will be used for enhanced targeting. A company data fee of $1 per company, per month will apply for any company included in active campaigns."
            confirmButtonLabel="Ok"
            handleClose={() => {
              hasShownCompanyFeeModal = true;
              setOpenModal(false);
            }}
            handleConfirm={() => {
              hasShownCompanyFeeModal = true;
              setOpenModal(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CompaniesDashboard;
